import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { applyPathLookupSettings } from 'components/Graph/commands/pathLookup/applyPathLookupSettings';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { selectGraphHasContent } from 'components/Graph/model/selectors/selectGraphHasContent';
import { selectGraphLoading } from 'components/Graph/model/selectors/selectGraphLoading';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { type FC, memo, useEffect, useMemo } from 'react';
import deepEqual from 'react-fast-compare';
import type { HostToDefaultGWFormValues } from '../GraphPathlookup/HostToDefaultGWFormValues';
import type { MulticastFormValues } from '../GraphPathlookup/MulticastFormValues';
import type { UnicastFormValues } from '../GraphPathlookup/UnicastFormValues';

interface Props {
  dataInputFormik:
    | FormikProps<UnicastFormValues>
    | FormikProps<MulticastFormValues>
    | FormikProps<HostToDefaultGWFormValues>;
}

const BarePathLookupApplySettings: FC<Props> = (props) => {
  const handleApplySettingsButtonClick = useStableCallback(() => {
    ctxProps.dispatch(applyPathLookupSettings);
    if (applySettingsToEmptyGraphEnabled) {
      props.dataInputFormik.submitForm();
    }
  });

  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    graphHasContent: selectGraphHasContent,
    presetDraft: prop('pathlookup', 'presetDraft'),
    dispatch: prop('dispatch'),
    graphLoading: selectGraphLoading,
    preset: prop('pathlookup', 'preset'),
    pathLookupApplied: selectPathLookupApplied,
  });

  // We do not compare all options because there are some options not affecting graph view.
  const existApplicableChanges: boolean = useMemo(
    () =>
      !deepEqual(
        {
          edges: ctxProps.presetDraft.edges,
          pathLookup: ctxProps.presetDraft.pathLookup,
        },
        {
          edges: ctxProps.preset.edges,
          pathLookup: ctxProps.preset.pathLookup,
        },
      ),
    [ctxProps.presetDraft, ctxProps.preset],
  );

  const applySettingsDisabled =
    !existApplicableChanges || ctxProps.graphLoading;
  const applySettingsToEmptyGraphEnabled =
    !ctxProps.graphHasContent && props.dataInputFormik.isValid;

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.dataInputFormik.validateForm): stable
  useEffect(() => {
    if (!ctxProps.graphHasContent) {
      props.dataInputFormik.validateForm();
    }
  }, [ctxProps.graphHasContent]);

  return (
    <div className='graph-side-panel-main-submit-button graph-side-panel-main-submit-button--inner-tab'>
      <Button
        disabled={applySettingsDisabled}
        onClick={handleApplySettingsButtonClick}
        size='sm'
        type='button'
        variant='primary'
      >
        {
          msg.canvasGraph.graphSidePanel.pathlookup.visualizationSetup
            .btnApplySettings
        }
      </Button>
    </div>
  );
};

export const PathLookupApplySettings = memo(BarePathLookupApplySettings);
